import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Popconfirm } from 'antd';
import {
    useGetDataGroupsQuery,
    useGetDataSourcesQuery,
    useUpdateStageStatusMutation,
    useSignOffGroupMutation,
    useDeleteDataSourceMutation,
    useDeleteDataGroupMutation,
    useToggleSourceFieldMutation,
    useInspectFileMutation,
    useUploadPortalFileMutation,
    usePullFromWarehouseMutation,
    usePullAllFromWarehouseMutation,
    useUpdateProcessingIssueMutation,
    useUpdateDataSourceMutation,
    useCreateDataGroupMutation,
    useCreateDataSourceMutation,
    useGetTemplatesQuery,
    useSaveTemplateMutation,
    useApplyTemplateMutation,
    useGetSourceCommentsQuery,
    useAddSourceCommentMutation,
} from '../../../redux/api/compassPortal';
import { StatusPill, sourceStatus, isOverdue, errMsg, downloadFile } from './bits';
import SourceDetail from './SourceDetail';

// Data Collection Tracker — the agent's state surface for stage 1. Users add data
// groups & sources; the table shows SOURCE / PULLED BY / SOURCE FILE / STATUS /
// UPLOADED BY / OWNER / TIMELINES. Rows group under their data group. Each source:
// upload a file (inspect-first), pull from the warehouse, mark received, flag an
// issue, manage owners, discuss. Mutations invalidate the RTK cache, so the lists
// refresh themselves; `refreshSignal` bumps after Bravo acts for an explicit refetch.

const CATEGORIES = ['Paid Digital Marketing', 'Influencer', 'CRM / Other', 'Organic', 'Offline'];

export default function DataCollectionTracker({ project, onBack, onComplete, refreshSignal }) {
    const pid = project.project_id;
    const token = useSelector((state) => state.oauth.auth0Token);

    const { data: groups, error: groupsError, refetch: refetchGroups } = useGetDataGroupsQuery({ projectId: pid });
    // Poll while any pull job runs — pull state lives on the source docs, so
    // progress survives navigation and refresh (the bar just re-reads it).
    const [pollPulls, setPollPulls] = useState(false);
    const { data: sources, error: sourcesError, refetch: refetchSources } = useGetDataSourcesQuery(
        { projectId: pid }, { pollingInterval: pollPulls ? 3000 : 0 });
    useEffect(() => {
        setPollPulls((sources || []).some((s) => s.pull_status === 'running' || s.pull_status === 'queued'));
    }, [sources]);

    const [updateStageStatus, { isLoading: completing }] = useUpdateStageStatusMutation();
    const [signOffGroup] = useSignOffGroupMutation();
    const [deleteDataSource] = useDeleteDataSourceMutation();
    const [deleteDataGroup] = useDeleteDataGroupMutation();
    const [toggleSourceField] = useToggleSourceFieldMutation();
    const [inspectFile] = useInspectFileMutation();
    const [uploadPortalFile] = useUploadPortalFileMutation();
    const [pullFromWarehouse] = usePullFromWarehouseMutation();
    const [pullAllFromWarehouse] = usePullAllFromWarehouseMutation();

    const [adding, setAdding] = useState(false);
    const [flagFor, setFlagFor] = useState(null); // source being flagged
    const [busyId, setBusyId] = useState(null);
    const [gateMsg, setGateMsg] = useState(null);
    const [commentsFor, setCommentsFor] = useState(null);
    const [ownerFor, setOwnerFor] = useState(null); // source whose ownership is being managed
    const [templating, setTemplating] = useState(false);
    const [reqDoc, setReqDoc] = useState(false); // client-facing Data Requirements doc
    const [pendingUpload, setPendingUpload] = useState(null); // { source, file, inspection, loading, err }
    const [notice, setNotice] = useState(null);
    const [collapsed, setCollapsed] = useState({}); // group_id -> true (fold a tracker section)
    const [detailFor, setDetailFor] = useState(null); // source open in the MS detail drawer

    // Re-fetch after Bravo acts (refreshSignal bumps) — no full remount.
    useEffect(() => { if (refreshSignal) { refetchGroups(); refetchSources(); } /* eslint-disable-next-line */ }, [refreshSignal]);

    const completeStage = async () => {
        setGateMsg(null);
        try {
            await updateStageStatus({ projectId: pid, stage_id: 'data-collection', status: 'complete' }).unwrap();
            onComplete?.('data-collection', 'data-processing');
        } catch (e) { setGateMsg(errMsg(e)); }
    };

    const signOff = async (g, on) => {
        setBusyId(g.group_id);
        try { await signOffGroup({ projectId: pid, groupId: g.group_id, signOff: on }).unwrap(); }
        catch (e) { setNotice(`⚠ ${errMsg(e)}`); }
        finally { setBusyId(null); }
    };

    const removeSource = async (s) => {
        setBusyId(s.source_id);
        try { await deleteDataSource({ projectId: pid, sourceId: s.source_id }).unwrap(); }
        catch (e) { setNotice(`⚠ ${errMsg(e)}`); }
        finally { setBusyId(null); }
    };
    const removeGroup = async (g) => {
        setBusyId(g.group_id);
        try { await deleteDataGroup({ projectId: pid, groupId: g.group_id }).unwrap(); }
        catch (e) { setNotice(`⚠ ${errMsg(e)}`); }
        finally { setBusyId(null); }
    };

    const toggleCollected = async (s) => {
        setBusyId(s.source_id);
        try { await toggleSourceField({ projectId: pid, sourceId: s.source_id, field: 'collected' }).unwrap(); }
        catch (e) { setNotice(`⚠ ${errMsg(e)}`); }
        finally { setBusyId(null); }
    };

    // Inspect the file first, show detected columns, then upload on confirm.
    const startUpload = async (s, file) => {
        if (!file) return;
        setPendingUpload({ source: s, file, loading: true });
        try {
            const inspection = await inspectFile({ projectId: pid, file }).unwrap();
            setPendingUpload({ source: s, file, inspection, loading: false });
        } catch (e) {
            setPendingUpload({ source: s, file, err: errMsg(e), loading: false });
        }
    };
    // Warehouse-backed source (CHA-366): pull the client's data instead of uploading.
    const pullWarehouse = async (s) => {
        setBusyId(s.source_id); setNotice(null);
        try {
            const res = await pullFromWarehouse({ projectId: pid, sourceId: s.source_id }).unwrap();
            setNotice(res?.total_rows
                ? `Pull started for "${s.name}" — ${Number(res.total_rows).toLocaleString()} rows on the way. Progress shows on the source row.`
                : `Pull started for "${s.name}" — progress shows on the source row.`);
        } catch (e) { setNotice(`⚠ ${errMsg(e)}`); }
        finally { setBusyId(null); }
    };

    const confirmUpload = async () => {
        const { source, file } = pendingUpload;
        setPendingUpload(null); setBusyId(source.source_id);
        try {
            const res = await uploadPortalFile({ projectId: pid, sourceId: source.source_id, file }).unwrap();
            const flags = res?.quality_flags || [];
            setNotice(flags.length
                ? `Uploaded — ${flags.length} thing${flags.length > 1 ? 's' : ''} we noticed: ${flags.join('; ')}`
                : 'Uploaded — file looks good.');
        } catch (e) { setNotice(`⚠ ${errMsg(e)}`); }
        finally { setBusyId(null); }
    };

    const download = async (s) => {
        try { await downloadFile(token, pid, s.source_id, 'original', s.file_name); }
        catch { setNotice(`⚠ Couldn't download the file for ${s.name}.`); }
    };

    const loadError = groupsError || sourcesError;
    if (loadError && !(groups && sources)) return <div className="cp-empty">Couldn't load the tracker — {errMsg(loadError)}</div>;
    if (!groups || !sources) return <div className="cp-empty"><span className="cp-spin" /></div>;

    const received = sources.filter((s) => s.collected || s.file_url).length;
    const pct = sources.length ? Math.round((received / sources.length) * 100) : 0;
    const allReceived = sources.length > 0 && received === sources.length;
    const allSigned = groups.length > 0 && groups.every((g) => g.signed_off);
    const canComplete = allReceived && allSigned;

    // group sources under their group
    const byGroup = groups.map((g) => ({ group: g, items: sources.filter((s) => s.group_id === g.group_id) }));
    const ungrouped = sources.filter((s) => !groups.some((g) => g.group_id === s.group_id));
    if (ungrouped.length) byGroup.push({ group: { group_id: '_', name: 'Other', category: '' }, items: ungrouped });

    return (
        <>
            <button className="cp-back-btn" onClick={onBack}>← Back to projects</button>

            <div className="cp-toolbar">
                <div style={{ minWidth: 240 }}>
                    <p className="cp-section-label" style={{ marginBottom: 6 }}>
                        {received} of {sources.length} sources received{groups.length ? ` · ${groups.filter((g) => g.signed_off).length}/${groups.length} groups signed off` : ''}
                    </p>
                    <div className="cp-progress"><span style={{ width: `${pct}%` }} /></div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button className="cp-btn cp-btn--ghost" onClick={() => setReqDoc(true)}>Data requirements</button>
                    <button className="cp-btn cp-btn--ghost" onClick={() => setTemplating(true)}>Templates</button>
                    <button className="cp-btn cp-btn--ghost" onClick={() => setAdding(true)}>+ Add data source</button>
                    {(sources || []).some((s) => s.pulled_by === 'charlie_oscar' && !(s.collected || s.file_url) && s.pull_status !== 'running' && s.pull_status !== 'queued') && (
                        <button className="cp-btn cp-btn--ghost" disabled={pollPulls}
                            onClick={async () => {
                                try {
                                    const r = await pullAllFromWarehouse({ projectId: pid }).unwrap();
                                    setNotice(r?.message || 'Pulling all warehouse sources, one at a time.');
                                } catch (e) { setNotice(`⚠ ${errMsg(e)}`); }
                            }}>Pull all warehouse sources</button>
                    )}
                    <button className="cp-btn cp-btn--primary" disabled={!canComplete || completing} onClick={completeStage}>
                        {completing ? 'Completing…' : 'Mark collection complete →'}
                    </button>
                </div>
            </div>
            {!canComplete && sources.length > 0 && (
                <p className="cp-sub" style={{ marginTop: -4, marginBottom: 16 }}>
                    {!allReceived ? `${sources.length - received} source(s) still to receive` : 'All received'}
                    {!allSigned ? ` · ${groups.filter((g) => !g.signed_off).length} group(s) need sign-off before this stage can complete.` : ''}
                </p>
            )}
            {gateMsg && <p style={{ color: 'var(--co-neg)', fontSize: 13, marginTop: -4 }}>{gateMsg}</p>}
            {notice && (
                <div className="cp-card" style={{ padding: '10px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 13 }}>{notice}</span>
                    <button className="cp-btn cp-btn--sm cp-btn--ghost" onClick={() => setNotice(null)}>Dismiss</button>
                </div>
            )}

            {sources.length === 0 ? (
                <div className="cp-empty">No data sources yet. Add the first source to build the tracker.</div>
            ) : (
                <div className="cp-table-wrap">
                    <table className="cp-table">
                        <thead>
                            <tr>
                                <th>Source</th><th>Pulled by</th><th>Source file</th><th>Status</th><th>Uploaded by</th><th>Owner</th><th>Timelines</th><th />
                            </tr>
                        </thead>
                        <tbody>
                            {byGroup.map(({ group, items }) => (
                                <React.Fragment key={group.group_id}>
                                    <tr className="cp-group-row"><td colSpan={8}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <button className="cp-group-fold" aria-expanded={!collapsed[group.group_id]}
                                                onClick={() => setCollapsed((c) => ({ ...c, [group.group_id]: !c[group.group_id] }))}>
                                                {collapsed[group.group_id] ? '▸' : '▾'}
                                            </button>
                                            <span style={{ cursor: 'pointer' }}
                                                onClick={() => setCollapsed((c) => ({ ...c, [group.group_id]: !c[group.group_id] }))}>
                                                {group.name}{group.category && group.category !== group.name ? ` · ${group.category}` : ''}
                                                {collapsed[group.group_id] ? ` · ${items.length} source${items.length === 1 ? '' : 's'}` : ''}
                                            </span>
                                            {group.group_id !== '_' && (
                                                group.signed_off ? (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                        <span className="cp-pill cp-pill--complete">Signed off{group.signed_off_by ? ` · ${group.signed_off_by}` : ''}</span>
                                                        <button className="cp-btn cp-btn--sm cp-btn--ghost" disabled={busyId === group.group_id} onClick={() => signOff(group, false)}>Reopen</button>
                                                    </span>
                                                ) : (
                                                    <button className="cp-btn cp-btn--sm cp-btn--ghost" disabled={busyId === group.group_id} onClick={() => signOff(group, true)}>Sign off group</button>
                                                )
                                            )}
                                            {group.group_id !== '_' && (
                                                <Popconfirm title={`Delete group "${group.name}" and all its sources?`} description="This can't be undone."
                                                    okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => removeGroup(group)}>
                                                    <button className="cp-btn cp-btn--sm cp-btn--ghost" disabled={busyId === group.group_id}>Delete group</button>
                                                </Popconfirm>
                                            )}
                                        </span>
                                    </td></tr>
                                    {!collapsed[group.group_id] && items.map((s) => (
                                        <tr key={s.source_id}>
                                            <td>
                                                <div className="cp-src-name cp-src-open" role="button" tabIndex={0}
                                                    onClick={() => setDetailFor(s)}
                                                    onKeyDown={(e) => e.key === 'Enter' && setDetailFor(s)}>{s.name}</div>
                                                {(s.pull_status === 'running' || s.pull_status === 'queued') && (
                                                    <div className="cp-pullbar" title="Pulling from the warehouse">
                                                        <div className="cp-pullbar-track"><span style={{ width: s.pull_status === 'queued' ? '0%' : `${s.pull_total_rows ? Math.min(100, Math.round(((s.pull_rows_done || 0) / s.pull_total_rows) * 100)) : 5}%` }} /></div>
                                                        <span className="cp-src-note">{s.pull_status === 'queued' ? 'queued' : `pulling · ${(s.pull_rows_done || 0).toLocaleString()}${s.pull_total_rows ? ` / ${Number(s.pull_total_rows).toLocaleString()}` : ''} rows`}</span>
                                                    </div>
                                                )}
                                                {s.pull_status === 'failed' && s.pull_error
                                                    ? <div className="cp-src-note" style={{ color: 'var(--co-neg)' }}>{s.pull_error}</div> : null}
                                                {s.requirements?.required_fields?.length ? (
                                                    <div className="cp-req-fields" title={s.requirements?.ask || ''}>
                                                        {s.requirements.required_fields.map((f) => <span key={f} className="cp-req-field">{f}</span>)}
                                                    </div>
                                                ) : null}
                                                {s.flags ? <div className="cp-src-note">⚑ {s.flags}</div> : null}
                                                {s.processing_issue_status === 'open' && s.processing_issue
                                                    ? <div className="cp-src-note" style={{ color: 'var(--co-neg)' }}>{s.processing_issue}</div> : null}
                                            </td>
                                            <td>
                                                {s.pulled_by === 'charlie_oscar'
                                                    ? <span className="cp-pull cp-pull--co">✓ We pull</span>
                                                    : <span className="cp-pull cp-pull--client">Client</span>}
                                            </td>
                                            <td>
                                                {s.file_url
                                                    ? <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                                                        <button className="cp-file-link" onClick={() => download(s)}>{s.file_name || 'Download file'}</button>
                                                        {s.upload_method === 'warehouse' && (
                                                            <button className="cp-btn cp-btn--sm cp-btn--ghost"
                                                                disabled={busyId === s.source_id || s.pull_status === 'running' || s.pull_status === 'queued'}
                                                                onClick={() => pullWarehouse(s)}>
                                                                {s.pull_status === 'running' || s.pull_status === 'queued' ? 'Pulling…' : 'Re-pull'}
                                                            </button>
                                                        )}
                                                    </div>
                                                    : s.upload_method === 'warehouse'
                                                        ? <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                                                            <button className="cp-btn cp-btn--sm" disabled={busyId === s.source_id || s.pull_status === 'running' || s.pull_status === 'queued'} onClick={() => pullWarehouse(s)}>{s.pull_status === 'running' || s.pull_status === 'queued' ? 'Pulling…' : 'Pull from warehouse'}</button>
                                                            {/* warehouse slots still accept a manual file — e.g. a
                                                                platform with no warehouse data yet */}
                                                            <UploadCell disabled={busyId === s.source_id || s.pull_status === 'running' || s.pull_status === 'queued'} onFile={(f) => startUpload(s, f)} />
                                                        </div>
                                                        : <UploadCell disabled={busyId === s.source_id} onFile={(f) => startUpload(s, f)} />}
                                            </td>
                                            <td><StatusPill status={sourceStatus(s)} /></td>
                                            <td className="cp-uploaded-by">{s.collected_by || (s.file_url ? '—' : '')}</td>
                                            <td className="cp-uploaded-by"><OwnerCell source={s} /></td>
                                            <td className="cp-due">
                                                {s.due_date
                                                    ? <span className={isOverdue(s.due_date) && !(s.collected || s.file_url) ? 'cp-due--overdue' : ''}>{s.due_date}</span>
                                                    : '—'}
                                            </td>
                                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {!s.file_url && (
                                                    <><button className="cp-btn cp-btn--sm cp-btn--ghost" disabled={busyId === s.source_id}
                                                        onClick={() => toggleCollected(s)}>
                                                        {s.collected ? 'Mark pending' : 'Mark received'}
                                                    </button>{' '}</>
                                                )}
                                                <button className="cp-btn cp-btn--sm cp-btn--ghost" onClick={() => setOwnerFor(s)}>Owner</button>{' '}
                                                <button className="cp-btn cp-btn--sm cp-btn--ghost" onClick={() => setFlagFor(s)}>Flag</button>{' '}
                                                <button className="cp-btn cp-btn--sm cp-btn--ghost" onClick={() => setCommentsFor(s)}>Discuss</button>{' '}
                                                <Popconfirm title={`Delete "${s.name}"?`} description="This can't be undone."
                                                    okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => removeSource(s)}>
                                                    <button className="cp-btn cp-btn--sm cp-btn--ghost" disabled={busyId === s.source_id}>Delete</button>
                                                </Popconfirm>
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {adding && (
                <AddSourceDrawer
                    groups={groups} pid={pid}
                    onClose={() => setAdding(false)}
                    onDone={() => setAdding(false)}
                />
            )}
            {flagFor && (
                <FlagDrawer
                    pid={pid} source={flagFor}
                    onClose={() => setFlagFor(null)}
                    onDone={(msg) => { setFlagFor(null); if (msg) setNotice(msg); }}
                />
            )}
            {commentsFor && (
                <CommentsDrawer pid={pid} source={commentsFor} onClose={() => setCommentsFor(null)} />
            )}
            {ownerFor && (
                <OwnerDrawer
                    pid={pid} source={ownerFor}
                    onClose={() => setOwnerFor(null)}
                    onDone={() => setOwnerFor(null)}
                />
            )}
            {templating && (
                <TemplateDrawer pid={pid} onClose={() => setTemplating(false)} onApplied={() => setTemplating(false)} />
            )}
            {reqDoc && (
                <RequirementsDoc project={project} groups={groups} sources={sources} onClose={() => setReqDoc(false)} />
            )}
            {pendingUpload && (
                <InspectModal pending={pendingUpload} onCancel={() => setPendingUpload(null)} onConfirm={confirmUpload} />
            )}
            {detailFor && (
                <SourceDetail projectId={pid} clientName={project?.client_name}
                    source={(sources || []).find((x) => x.source_id === detailFor.source_id) || detailFor}
                    onClose={() => setDetailFor(null)} />
            )}
        </>
    );
}

function InspectModal({ pending, onCancel, onConfirm }) {
    const sheet = pending.inspection?.sheets?.[0];
    return (
        <>
            <div className="cp-drawer-backdrop" onClick={onCancel} />
            <div className="cp-drawer" role="dialog" aria-label="Check file before upload">
                <h3>Check before upload — {pending.source.name}</h3>
                <p className="cp-sub" style={{ marginTop: 0 }}>{pending.file?.name}</p>
                {pending.loading && <p className="cp-sub"><span className="cp-spin" /> Reading the file…</p>}
                {pending.err && <p style={{ color: 'var(--co-neg)', fontSize: 13 }}>Couldn't read it: {pending.err}. You can still upload.</p>}
                {sheet && (
                    <div className="cp-card" style={{ padding: 14, margin: '10px 0' }}>
                        <div className="cp-uploaded-by">{sheet.row_count} rows · {sheet.columns.length} columns</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                            {sheet.columns.map((c) => <span key={c} className="cp-pill cp-pill--awaiting">{c}</span>)}
                        </div>
                        {sheet.date_columns && Object.keys(sheet.date_columns).length > 0 && (
                            <p className="cp-sub" style={{ fontSize: 12, marginTop: 8 }}>
                                Date range: {Object.entries(sheet.date_columns).map(([c, r]) => `${c} ${r.min}→${r.max}`).join(', ')}
                            </p>
                        )}
                    </div>
                )}
                <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                    <button className="cp-btn cp-btn--primary" disabled={pending.loading} onClick={onConfirm}>Looks right — upload</button>
                    <button className="cp-btn cp-btn--ghost" onClick={onCancel}>Cancel</button>
                </div>
            </div>
        </>
    );
}

function CommentsDrawer({ pid, source, onClose }) {
    const [text, setText] = useState('');
    const { data: comments } = useGetSourceCommentsQuery({ projectId: pid, sourceId: source.source_id });
    const [addComment, { isLoading: busy }] = useAddSourceCommentMutation();
    const send = async () => {
        if (!text.trim()) return;
        try { await addComment({ projectId: pid, sourceId: source.source_id, text: text.trim() }).unwrap(); setText(''); }
        catch { /* keep the text so they can retry */ }
    };
    return (
        <>
            <div className="cp-drawer-backdrop" onClick={onClose} />
            <div className="cp-drawer" role="dialog" aria-label="Discussion">
                <h3>Discussion — {source.name}</h3>
                <div style={{ margin: '12px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {comments === undefined ? <span className="cp-spin" />
                        : (comments || []).length === 0 ? <p className="cp-sub">No comments yet.</p>
                            : comments.map((c) => (
                                <div key={c.comment_id} className="cp-card" style={{ padding: 12 }}>
                                    <div className="cp-uploaded-by">{c.author}</div>
                                    <div style={{ fontSize: 14, marginTop: 4 }}>{c.text}</div>
                                </div>
                            ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()}
                        placeholder="Add a comment…" style={{ flex: 1, padding: '10px 12px', borderRadius: 'var(--co-radius-sm)', border: '1px solid rgba(20,22,21,0.2)', font: 'inherit' }} />
                    <button className="cp-btn cp-btn--primary cp-btn--sm" disabled={busy || !text.trim()} onClick={send}>Send</button>
                </div>
                <button className="cp-btn cp-btn--ghost" style={{ marginTop: 16 }} onClick={onClose}>Close</button>
            </div>
        </>
    );
}

function TemplateDrawer({ pid, onClose, onApplied }) {
    const [name, setName] = useState('');
    const [err, setErr] = useState(null);
    const { data: templates } = useGetTemplatesQuery();
    const [applyTemplate, { isLoading: applying }] = useApplyTemplateMutation();
    const [saveTemplate, { isLoading: saving }] = useSaveTemplateMutation();
    const busy = applying || saving;
    const apply = async (t) => {
        setErr(null);
        try { await applyTemplate({ projectId: pid, templateId: t.template_id }).unwrap(); onApplied(); }
        catch (e) { setErr(errMsg(e)); }
    };
    const save = async () => {
        if (!name.trim()) return;
        setErr(null);
        try { await saveTemplate({ projectId: pid, name: name.trim() }).unwrap(); setName(''); }
        catch (e) { setErr(errMsg(e)); }
    };
    return (
        <>
            <div className="cp-drawer-backdrop" onClick={onClose} />
            <div className="cp-drawer" role="dialog" aria-label="Templates">
                <h3>Project templates</h3>
                <p className="cp-sub" style={{ marginTop: 0 }}>Reuse a data-source skeleton across clients.</p>
                <p className="cp-section-label">Apply a template</p>
                {templates === undefined ? <span className="cp-spin" />
                    : (templates || []).length === 0 ? <p className="cp-sub">No templates saved yet.</p>
                        : templates.map((t) => (
                            <div key={t.template_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                                <span>{t.name} <span className="cp-uploaded-by">· {t.n_groups} groups · {t.n_sources} sources</span></span>
                                <button className="cp-btn cp-btn--sm cp-btn--primary" disabled={busy} onClick={() => apply(t)}>Apply</button>
                            </div>
                        ))}
                <p className="cp-section-label" style={{ marginTop: 16 }}>Save this project as a template</p>
                <div style={{ display: 'flex', gap: 8 }}>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name"
                        style={{ flex: 1, padding: '10px 12px', borderRadius: 'var(--co-radius-sm)', border: '1px solid rgba(20,22,21,0.2)', font: 'inherit' }} />
                    <button className="cp-btn cp-btn--ghost cp-btn--sm" disabled={busy || !name.trim()} onClick={save}>Save</button>
                </div>
                {err && <p style={{ color: 'var(--co-neg)', fontSize: 13 }}>{err}</p>}
                <button className="cp-btn cp-btn--ghost" style={{ marginTop: 16 }} onClick={onClose}>Close</button>
            </div>
        </>
    );
}

// email → short handle for the compact Owner column (full address on hover).
function ownerShort(email) {
    if (!email) return null;
    return email.includes('@') ? email.split('@')[0] : email;
}

function OwnerCell({ source }) {
    const owner = source.owner;
    const extra = (source.owners || []).filter((o) => o && o !== owner);
    if (!owner) return <span style={{ color: 'var(--co-grey)' }}>Unassigned</span>;
    return (
        <span title={[owner, ...extra].join(', ')}>
            {ownerShort(owner)}{extra.length ? ` +${extra.length}` : ''}
        </span>
    );
}

// Manage who's accountable for a dataset. The uploader is captured as owner
// automatically; here you can change the primary owner or add/remove others.
function OwnerDrawer({ pid, source, onClose, onDone }) {
    const [owner, setOwner] = useState(source.owner || '');
    const [owners, setOwners] = useState(source.owners || []);
    const [add, setAdd] = useState('');
    const [err, setErr] = useState(null);
    const [updateSource, { isLoading: busy }] = useUpdateDataSourceMutation();

    const addOwner = () => {
        const e = add.trim().toLowerCase();
        if (!e) return;
        if (e === owner.trim().toLowerCase() || owners.includes(e)) { setAdd(''); return; }
        setOwners([...owners, e]); setAdd('');
    };
    const save = async () => {
        const primary = owner.trim();
        if (!primary) { setErr('Give the dataset a primary owner (an email).'); return; }
        setErr(null);
        try {
            await updateSource({
                projectId: pid, sourceId: source.source_id,
                owner: primary,
                owners: owners.filter((o) => o && o !== primary.toLowerCase()),
            }).unwrap();
            onDone();
        } catch (e) { setErr(errMsg(e)); }
    };

    return (
        <>
            <div className="cp-drawer-backdrop" onClick={onClose} />
            <div className="cp-drawer" role="dialog" aria-label="Manage ownership">
                <h3>Ownership — {source.name}</h3>
                <p className="cp-sub" style={{ marginTop: 0 }}>
                    The owner is accountable for this dataset and is who gets chased when it's flagged.
                    Whoever uploads the file becomes the owner automatically.
                </p>
                <label className="cp-section-label">Primary owner (email)</label>
                <input value={owner} onChange={(e) => setOwner(e.target.value)} style={inp} placeholder="name@charlieoscar.com" />
                <label className="cp-section-label">Additional owners</label>
                {owners.length === 0 && <p className="cp-sub" style={{ marginTop: 0 }}>None yet.</p>}
                {owners.map((o) => (
                    <div key={o} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
                        <span style={{ fontSize: 14 }}>{o}</span>
                        <button className="cp-btn cp-btn--sm cp-btn--ghost" onClick={() => setOwners(owners.filter((x) => x !== o))}>Remove</button>
                    </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <input value={add} onChange={(e) => setAdd(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addOwner()}
                        placeholder="Add another owner…" style={{ flex: 1, padding: '10px 12px', borderRadius: 'var(--co-radius-sm)', border: '1px solid rgba(20,22,21,0.2)', font: 'inherit' }} />
                    <button className="cp-btn cp-btn--ghost cp-btn--sm" onClick={addOwner}>Add</button>
                </div>
                {err && <p style={{ color: 'var(--co-neg)', fontSize: 13 }}>{err}</p>}
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    <button className="cp-btn cp-btn--primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save ownership'}</button>
                    <button className="cp-btn cp-btn--ghost" onClick={onClose}>Cancel</button>
                </div>
            </div>
        </>
    );
}

// Client-facing Data Requirements doc, built from the project's sources' template
// requirements. Per Tobi's design: "provide the following per source; items marked
// ✓ we pull ourselves — no action needed." Printable / copyable.
function RequirementsDoc({ project, groups, sources, onClose }) {
    const byGroup = groups.map((g) => ({ group: g, items: sources.filter((s) => s.group_id === g.group_id) }))
        .filter((x) => x.items.length);
    return (
        <>
            <div className="cp-drawer-backdrop" onClick={onClose} />
            <div className="cp-drawer" role="dialog" aria-label="Data requirements">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                    <h3>{project?.client_name} — Data Requirements</h3>
                    <button className="cp-btn cp-btn--sm cp-btn--ghost" onClick={() => window.print()}>Print / save</button>
                </div>
                <p className="cp-sub" style={{ marginTop: 0 }}>
                    Please provide the following for each source below. Items marked <strong>✓ we pull ourselves</strong> — no action needed on those.
                </p>
                {byGroup.map(({ group, items }) => (
                    <div key={group.group_id} style={{ marginTop: 18 }}>
                        <p className="cp-section-label" style={{ marginBottom: 6 }}>{group.name}</p>
                        {items.map((s) => (
                            <div key={s.source_id} className="cp-card" style={{ padding: 12, marginBottom: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                                    <strong style={{ fontSize: 14 }}>{s.name}</strong>
                                    {s.pulled_by === 'charlie_oscar'
                                        ? <span className="cp-pull cp-pull--co">✓ We pull</span>
                                        : <span className="cp-pull cp-pull--client">Your action</span>}
                                </div>
                                {s.requirements?.ask ? <div className="cp-req" style={{ marginTop: 4 }}>{s.requirements.ask}</div> : null}
                                {s.requirements?.required_fields?.length ? (
                                    <div className="cp-req-fields">
                                        {s.requirements.required_fields.map((f) => <span key={f} className="cp-req-field">{f}</span>)}
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                ))}
                <button className="cp-btn cp-btn--ghost" style={{ marginTop: 16 }} onClick={onClose}>Close</button>
            </div>
        </>
    );
}

function UploadCell({ onFile, disabled }) {
    const ref = useRef(null);
    return (
        <>
            <button className="cp-btn cp-btn--sm" disabled={disabled} onClick={() => ref.current?.click()}>
                {disabled ? 'Uploading…' : 'Upload'}
            </button>
            <input ref={ref} type="file" accept=".csv,.xlsx,.xls" hidden
                onChange={(e) => onFile(e.target.files?.[0])} />
        </>
    );
}

function AddSourceDrawer({ groups, pid, onClose, onDone }) {
    const [name, setName] = useState('');
    const [groupId, setGroupId] = useState(groups[0]?.group_id || 'new');
    const [newCat, setNewCat] = useState(CATEGORIES[0]);
    const [due, setDue] = useState('');
    const [method, setMethod] = useState('file'); // 'file' | 'warehouse'
    const [file, setFile] = useState(null); // the actual file to attach (method === 'file')
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const fileRef = useRef(null);

    const [createGroup] = useCreateDataGroupMutation();
    const [createSource] = useCreateDataSourceMutation();
    const [uploadPortalFile] = useUploadPortalFileMutation();

    const submit = async () => {
        if (!name.trim()) { setErr('Give the source a name.'); return; }
        setBusy(true); setErr(null);
        try {
            let gid = groupId;
            if (groupId === 'new') {
                const g = await createGroup({ projectId: pid, name: newCat, category: newCat, purpose: `${newCat} data` }).unwrap();
                gid = g.group_id;
            }
            const src = await createSource({ projectId: pid, name: name.trim(), group_id: gid, due_date: due || undefined, upload_method: method, pulled_by: method === 'warehouse' ? 'charlie_oscar' : 'client' }).unwrap();
            // If they attached a file, upload it now so "Upload a file" actually uploads
            // (you can also skip it here and attach later from the source's row).
            if (method === 'file' && file && src?.source_id) {
                await uploadPortalFile({ projectId: pid, sourceId: src.source_id, file }).unwrap();
            }
            onDone();
        } catch (e) { setErr(errMsg(e)); setBusy(false); }
    };

    return (
        <>
            <div className="cp-drawer-backdrop" onClick={onClose} />
            <div className="cp-drawer" role="dialog" aria-label="Add data source">
                <h3>Add data source</h3>
                <label className="cp-section-label">Category / group</label>
                <select value={groupId} onChange={(e) => setGroupId(e.target.value)} style={inp}>
                    {groups.map((g) => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
                    <option value="new">+ New category…</option>
                </select>
                {groupId === 'new' && (
                    <select value={newCat} onChange={(e) => setNewCat(e.target.value)} style={inp}>
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                )}
                <label className="cp-section-label">Source name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} style={inp} placeholder="e.g. Meta ads spend" />
                <label className="cp-section-label">How will this data arrive?</label>
                <div style={{ display: 'flex', gap: 8, margin: '4px 0 16px' }}>
                    <button type="button" className={`cp-btn cp-btn--sm ${method === 'file' ? 'cp-btn--primary' : 'cp-btn--ghost'}`} onClick={() => setMethod('file')}>Upload a file</button>
                    <button type="button" className={`cp-btn cp-btn--sm ${method === 'warehouse' ? 'cp-btn--primary' : 'cp-btn--ghost'}`} onClick={() => setMethod('warehouse')}>Pull from warehouse</button>
                </div>
                {method === 'warehouse' && (
                    <p className="cp-sub" style={{ marginTop: -8, marginBottom: 16 }}>
                        We'll pull this client's daily campaign data straight from the data warehouse — no upload needed.
                    </p>
                )}
                {method === 'file' && (
                    <div style={{ marginTop: -8, marginBottom: 16 }}>
                        <button type="button" className="cp-btn cp-btn--sm cp-btn--ghost" onClick={() => fileRef.current?.click()}>
                            {file ? 'Change file' : 'Choose a file…'}
                        </button>
                        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" hidden
                            onChange={(e) => setFile(e.target.files?.[0] || null)} />
                        <span className="cp-sub" style={{ marginLeft: 10 }}>
                            {file ? file.name : 'CSV or Excel. Optional — you can also add it later from the tracker.'}
                        </span>
                    </div>
                )}
                <label className="cp-section-label">Due date (optional)</label>
                <input type="date" value={due} onChange={(e) => setDue(e.target.value)} style={inp} />
                {err && <p style={{ color: 'var(--co-neg)', fontSize: 13 }}>{err}</p>}
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    <button className="cp-btn cp-btn--primary" disabled={busy} onClick={submit}>{busy ? 'Adding…' : 'Add source'}</button>
                    <button className="cp-btn cp-btn--ghost" onClick={onClose}>Cancel</button>
                </div>
            </div>
        </>
    );
}

function FlagDrawer({ pid, source, onClose, onDone }) {
    const [text, setText] = useState(source.processing_issue || '');
    const owner = source.owner || source.collected_by || '';
    const [notify, setNotify] = useState(!!owner);
    const [err, setErr] = useState(null);
    const [updateProcessingIssue, { isLoading: busy }] = useUpdateProcessingIssueMutation();

    const save = async (status) => {
        setErr(null);
        const willNotify = status === 'open' && notify && !!owner;
        try {
            const res = await updateProcessingIssue({
                projectId: pid, sourceId: source.source_id,
                issue_text: text.trim(), status, notify: willNotify, owner: owner || '', source_name: source.name || '',
            }).unwrap();
            let msg;
            if (status === 'resolved') msg = 'Flag resolved.';
            else if (!willNotify) msg = 'Flag raised.';
            else {
                const n = res && res._notice;
                if (n && n.notified) msg = `Flag raised — the owner (${n.notified}) has been notified.`;
                else if (n && n.reason === 'no_owner') msg = 'Flag raised — no owner assigned, so nobody was notified.';
                else msg = `Flag raised — notifying ${owner}.`;
            }
            onDone(msg);
        } catch (e) { setErr(errMsg(e)); }
    };

    return (
        <>
            <div className="cp-drawer-backdrop" onClick={onClose} />
            <div className="cp-drawer" role="dialog" aria-label="Flag a requirement">
                <h3>Flag — {source.name}</h3>
                <p className="cp-sub" style={{ marginTop: 0 }}>
                    Raise an issue or a new requirement to whoever owns this dataset.
                    Raising a flag re-opens the source for collection.
                </p>
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} style={{ ...inp, resize: 'vertical' }}
                    placeholder="e.g. Need daily granularity, not weekly; missing Nov data." />
                {owner ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '4px 0 12px' }}>
                        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
                        Notify the owner (<strong>{owner}</strong>) about this flag
                    </label>
                ) : (
                    <p className="cp-sub" style={{ margin: '4px 0 12px', color: 'var(--co-neg)' }}>
                        No owner is assigned, so nobody can be notified. Set an owner (via the Owner button) to notify them automatically.
                    </p>
                )}
                {err && <p style={{ color: 'var(--co-neg)', fontSize: 13 }}>{err}</p>}
                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                    <button className="cp-btn cp-btn--primary" disabled={busy || !text.trim()} onClick={() => save('open')}>Raise flag</button>
                    <button className="cp-btn cp-btn--ghost" disabled={busy} onClick={() => save('resolved')}>Resolve</button>
                    <button className="cp-btn cp-btn--ghost" onClick={onClose}>Close</button>
                </div>
            </div>
        </>
    );
}

const inp = {
    width: '100%', padding: '10px 12px', margin: '4px 0 16px', borderRadius: 'var(--co-radius-sm)',
    border: '1px solid rgba(20,22,21,0.2)', font: 'inherit', background: '#fff',
};
